import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';
dayjs.extend(isBetween);
dayjs.extend(utc);

// Hooks
import useSocialProfiles from '@/utils/hooks/swr/useSocialProfiles';
import useUser from '@/utils/hooks/swr/useUser';
// Components
import useUserFBPermissions from '@/utils/hooks/swr/useUserFBPermissions';
import useUserRole from '@/utils/hooks/user/useUserRole';

export type BannersTypes = ReturnType<typeof useGetGlobalInfo>;

const useGetGlobalInfo = () => {
    const { user } = useUser();
    const { isTeamMember } = useUserRole();
    const { user: userFBPermissions, isLoading } = useUserFBPermissions();
    const {
        activatedSocialProfiles,
        errorAuthDataAccessSocialProfiles,
        errorAuthOtherSocialProfiles
    } = useSocialProfiles();

    const hasReAuthError = Boolean(
        errorAuthDataAccessSocialProfiles?.length || errorAuthOtherSocialProfiles?.length
    );

    const partialHistoryAvailable = activatedSocialProfiles?.filter(
        (profile) =>
            profile.first_stats &&
            !profile.full_stats &&
            dayjs().utc().diff(dayjs(profile.created_at), 'days') < 2
    );

    const banners = {
        noActivePlan: {
            type: 'error',
            isActive: !user?.subscribed_or_trial
        },
        pastDue: {
            type: 'error',
            isActive: user?.past_due
        },
        reAuthSocialProfile: {
            type: 'error',
            isActive: hasReAuthError
        },
        dataAccessExpired: {
            type: 'error',
            isActive:
                !isLoading &&
                !isTeamMember &&
                !userFBPermissions?.not_applicable &&
                (userFBPermissions?.refresh_date === null ||
                    dayjs(userFBPermissions?.refresh_date).diff(dayjs()) <= 0)
        },
        reminderDataAccessExpired: {
            type: 'error',
            isActive:
                !isLoading &&
                !isTeamMember &&
                !userFBPermissions?.not_applicable &&
                userFBPermissions?.refresh_date !== null &&
                dayjs().isBetween(
                    dayjs(userFBPermissions?.refresh_date).subtract(5, 'day'),
                    dayjs(userFBPermissions?.refresh_date)
                )
        },
        partialHistoryAvailable: {
            type: 'info',
            isActive: Boolean(partialHistoryAvailable.length)
        }
    } as const;

    const activeBannersInfoArray = Object.values(banners).filter((banner) => banner.isActive);
    const isErrorsBanners = Boolean(
        activeBannersInfoArray.find((banner) => banner.type === 'error')
    );

    const isInfoBanners = Boolean(
        !isErrorsBanners && activeBannersInfoArray.find((banner) => banner.type === 'info')
    );

    let bannersType: (typeof banners)[keyof typeof banners]['type'] = 'error';

    if (isInfoBanners) {
        bannersType = 'info';
    }

    return { banners, bannersType, nbActiveBanners: activeBannersInfoArray.length };
};

export default useGetGlobalInfo;
